import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';

import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import * as POSSelectors from 'PointOfSale/selectors';
import { TransactionTypeEnum } from 'Common/Constants/Enumeration/TransactionTypeEnum';
import ServingComponent from 'PointOfSale/modules/serving/component/ServingComponent';
import LookUpContainer from 'PointOfSale/modules/lookup/container/LookUpContainer';
import { processTransaction } from 'PointOfSale/state/actions';
import {
  POS_ROUTES,
  INVALID_STUDENT,
  SessionTypes,
  QueueStatusEnum,
} from 'PointOfSale/modules/common/constants';
import {
  changeQueueStatus,
  shuffleQueue,
} from 'PointOfSale/modules/queue/state/actions';
import TimerButton from 'Common/Components/TimerButton';
import {
  addQuickServeItem,
  setLastTransactionProfile,
} from 'PointOfSale/modules/individualServing/state/actions';
import { isGenericAccount } from 'PointOfSale/modules/common/helpers';
import { setNotificationDialog } from 'Components/dialogs/state/actions';

import 'PointOfSale/styles/components/serving.less';

class ServingContainer extends Component {
  timer = React.createRef();

  get hasTimer() {
    return Boolean(this.timer && this.timer.current);
  }

  isInvalidProfile(profile) {
    return !profile.Id || profile.Id === INVALID_STUDENT.Id;
  }

  componentDidMount() {
    const {
      changeQueueStatus,
      queueStatus,
      queueStartedPreviously,
      profile,
    } = this.props;

    if (
      !this.isInvalidProfile(profile) &&
      (queueStatus === QueueStatusEnum.ON ||
        (queueStatus === QueueStatusEnum.PAUSED && queueStartedPreviously))
    ) {
      this.toggleSession(QueueStatusEnum.ON);
    } else {
      changeQueueStatus(QueueStatusEnum.PAUSED);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      queueStatus,
      changeQueueStatus,
      profile,
      queueStartedPreviously,
    } = this.props;

    // Queue has been updated, next student is now in position
    if (prevProps.profile !== profile) {
      if (this.isInvalidProfile(profile)) {
        changeQueueStatus(QueueStatusEnum.PAUSED);
        return;
      }
      if (
        queueStatus === QueueStatusEnum.ON ||
        (queueStatus === QueueStatusEnum.PAUSED && queueStartedPreviously)
      ) {
        this.toggleSession(QueueStatusEnum.ON);
      }
    }

    // Autoserve has been restarted, start timer
    if (
      prevProps.queueStatus !== QueueStatusEnum.ON &&
      queueStatus === QueueStatusEnum.ON
    ) {
      this.toggleSession(QueueStatusEnum.ON);
    }
  }

  componentWillUnmount() {
    if (this.hasTimer) {
      this.timer.current.stop();
    }
    if (this.props.queueStatus === QueueStatusEnum.ON) {
      this.props.changeQueueStatus(QueueStatusEnum.PAUSED);
    }
  }

  checkForGenerics = profile => {
    const { setNotificationDialog, shuffleQueue } = this.props;
    const isGeneric = isGenericAccount(profile);
    if (isGeneric) {
      const dialog = {
        ...NOTIFICATION_DIALOG_MESSAGES.GENERIC_ACCOUNTS_CASH_ONLY,
      };

      _.defer(shuffleQueue, 0);

      setTimeout(() => {
        /*
          dialog can interfere with page transitions
          moved setNotificationDialog into a setTimeout
          to make it show after the page had transitioned into individual serving
        */
        setNotificationDialog(dialog);
      }, 200);
    }
    return isGeneric;
  };

  checkForWithdrawn = profile => {
    const { setNotificationDialog, shuffleQueue } = this.props;
    const isWithdrawn = profile.StatusCurrent === '5';
    if (isWithdrawn) {
      const dialog = {
        ...NOTIFICATION_DIALOG_MESSAGES.WITHDRAWN_STUDENT_SERVE_ATTEMPT,
      };

      _.defer(shuffleQueue, 0);

      setTimeout(() => {
        /*
          dialog can interfere with page transitions
          moved setNotificationDialog into a setTimeout
          to make it show after the page had transitioned into individual serving
        */
        setNotificationDialog(dialog);
      }, 200);
    }
    return isWithdrawn;
  };

  completeServing = async () => {
    const { processTransaction, profile, transaction, history } = this.props;

    const isGeneric = this.checkForGenerics(profile);
    const isWithdrawn = this.checkForWithdrawn(profile);

    if (isGeneric || isWithdrawn) {
      return;
    }

    transaction.TransactionType = TransactionTypeEnum.Charge;
    const [success] = await processTransaction(transaction);

    if (!success) {
      // On write failure or error
      history.replace(`${POS_ROUTES.QUEUE}/${transaction.StudentIdSeed}`);
    }
  };

  toggleSession = status => {
    const {
      activeSession,
      sessions,
      changeQueueStatus,
      shuffleQueue,
      addQuickServeItem,
      queueStatus,
      profile,
    } = this.props;

    const isGeneric = this.checkForGenerics(profile);

    if (isGeneric) {
      return;
    }

    if (queueStatus !== status) {
      changeQueueStatus(status);
    }

    if (status === QueueStatusEnum.ON) {
      addQuickServeItem();
      if (this.hasTimer) {
        this.timer.current.restart();
      }
    } else {
      if (
        activeSession === SessionTypes.Queue &&
        _.get(sessions, [activeSession, 'autoServe'])
      ) {
        shuffleQueue(0);
      }
    }
  };

  render() {
    const {
      activeSession,
      sessions,
      showLookUp,
      queueStatus,
      profile,
      queue,
    } = this.props;

    const activeSessionDetails = sessions[activeSession];

    const interval =
      Number(_.get(activeSessionDetails, 'timeInterval', 0)) * 1000;

    const currentStudent = profile || {};

    return (
      <React.Fragment>
        {showLookUp ? (
          <LookUpContainer />
        ) : (
          <React.Fragment>
            <div className="auto-serve-profile-wrapper">
              <ServingComponent currentStudent={currentStudent} />
              <TimerButton
                ref={this.timer}
                paused={queueStatus !== QueueStatusEnum.ON}
                className="auto-serve-button"
                onToggle={() =>
                  this.toggleSession(
                    queueStatus !== QueueStatusEnum.ON
                      ? QueueStatusEnum.ON
                      : QueueStatusEnum.PAUSED
                  )
                }
                label={queueStatus !== QueueStatusEnum.ON ? 'Start' : 'Pause'}
                interval={interval}
                hidden={queue.length < 1}
                onTick={this.completeServing}
              />
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

ServingContainer.propTypes = {
  queue: PropTypes.array,
  sessions: PropTypes.object,
  activeSession: PropTypes.string,
  queueStatus: PropTypes.string,
  showLookUp: PropTypes.bool,
};

const mapStateToProps = state => ({
  profile: POSSelectors.getProfile(state),
  queue: POSSelectors.getQueue(state),
  sessions: POSSelectors.getSessions(state),
  activeSession: POSSelectors.getActiveSession(state),
  showLookUp: POSSelectors.getShowLookUp(state),
  transaction: POSSelectors.getCurrentTransaction(state),
  queueStatus: POSSelectors.getQueueStatus(state),
  queueStartedPreviously: POSSelectors.getQueueStartedPreviously(state),
});

const mapDispatchToProps = {
  changeQueueStatus,
  shuffleQueue,
  addQuickServeItem,
  setLastTransactionProfile,
  setNotificationDialog,
  processTransaction,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ServingContainer)
);



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/serving/container/index.js